<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold" v-if="form.id">
                                                Update Offer `{{ form.name }}`
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white font-bold" v-else>
                                                Create New Offer
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                Get started by filling in the information below to create a new
                                                quote.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div>
                                                    <label for="quote-name"
                                                           class="block text-sm text-gray-900 required">
                                                        Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="quote-name" v-model="form.name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.name}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="client-name"
                                                           class="block text-sm text-gray-900">
                                                        Buy Currency
                                                    </label>
                                                    <div class="mt-1 mb-3">
                                                        <v-select class="h-full mt-1" :options="currencies" label="name" :reduce="currencies => currencies.id" v-model="form.buy_currency_id" placeholder="Select Currency" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="client-name"
                                                           class="block text-sm text-gray-900">
                                                        Sell Currency
                                                    </label>
                                                    <div class="mt-1 mb-3">
                                                        {{ this.project.bank_account.currency.name }} - {{ this.project.bank_account.currency.symbol }}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="quote-exchange-rate"
                                                           class="block text-sm text-gray-900">
                                                        Exchange Rate
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="number" step="0.00001" id="quote-name" v-model="form.exchange_rate"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.exchange_rate}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label
                                                        class="block text-sm text-gray-900">
                                                        Status
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="client-name" v-model="form.status"
                                                                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-light-pitch-purple sm:text-sm rounded-md">
                                                            <option disabled value="">Select a status</option>
                                                            <option v-for="(status, index) in quote_statuses"
                                                                    :value="status" v-html="status"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive, computed} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, ExclamationCircleIcon, ArrowCircleRightIcon, RefreshIcon} from '@heroicons/vue/solid'
import {useForm, usePage} from '@inertiajs/vue3'
import Inertia from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import {mapState, mapActions} from 'vuex'


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        ExclamationCircleIcon,
        XIcon,
        ArrowCircleRightIcon,
        RefreshIcon
    },
    computed: {
        ...mapState({
            organisations: state => state.organisations.clients ?? null,
        })
    },
    data() {
        return {
            show: ref(false),
            showErrors: ref(false),
            clientOverride: ref(false),
            project: computed(() => usePage().props.project),
            quote_statuses: usePage().props.quote_statuses,
            currencies: usePage().props.currencies,
            updating: false,
            form: useForm({
                id: null,
                name: null,
                client_id: usePage().props.project.client.id,
                client: usePage().props.project.client.name,
                status: "Request",
                project_id: usePage().props.project.id,
                exchange_rate: 1,
                buy_currency_id: usePage().props.project.bank_account.currency_id
            }),
        }
    },
    mounted() {
        this.getAllClients();
    },
    methods: {
        getAllClients() {
            this.$store.dispatch('organisations/getAllClients');
        },
        overrideClient() {
          this.clientOverride = true
        },
        revertToProjectClient() {
            this.clientOverride = false
            this.form.client_id = this.project.client.id
        },
        submit() {
            if (this.updating) {
                this.form.post(route('frontend.project.quote.update', [this.project.id, this.form.id]), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();

                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            } else {
                this.form.post(route('frontend.project.quote.create', this.project.id), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            }
        },
        close() {
            this.form.reset();
            this.form.clearErrors();
            this.show = false;
        },

        open() {
            this.form.clearErrors();
            this.show = true;
        }
    }
}
</script>
